import type { ColumnsType } from 'antd/es/table';
import type { TableProps } from 'antd';
import type { SorterResult, SortOrder } from 'antd/es/table/interface';

export interface ServerSortRequest {
  PageIndex?: number;
  SortKeySelector?: string;
  SortOrder?: string;
}

export interface ServerSortDefaults {
  SortKeySelector: string;
  SortOrder: 'Asc' | 'Desc';
}

const SORT_DIRECTIONS: SortOrder[] = ['ascend', 'descend', 'ascend'];

const getSorterColumnKey = <TRecord>(
  sorter: SorterResult<TRecord>,
): string | undefined => {
  if (typeof sorter.columnKey === 'string') return sorter.columnKey;
  if (typeof sorter.field === 'string') return sorter.field;
  return undefined;
};

const getActiveSorter = <TRecord>(
  sorter: SorterResult<TRecord> | SorterResult<TRecord>[],
): SorterResult<TRecord> | undefined => {
  if (Array.isArray(sorter)) {
    return sorter.find((item) => Boolean(item.order));
  }

  return sorter.order ? sorter : undefined;
};

const getColumnKey = <TRecord>(
  column: ColumnsType<TRecord>[number],
): string | undefined => {
  if (typeof column.key === 'string') return column.key;

  if ('dataIndex' in column && typeof column.dataIndex === 'string') {
    return column.dataIndex;
  }

  return undefined;
};

const hasOwnKey = <T extends object, K extends PropertyKey>(
  object: T,
  key: K,
): key is K & keyof T => Object.prototype.hasOwnProperty.call(object, key);

const isColumnSelectorKey = <TColumnMap extends Record<string, string>>(
  columnSelectorMap: TColumnMap,
  columnKey: string,
): columnKey is keyof TColumnMap & string =>
  hasOwnKey(columnSelectorMap, columnKey);

export const toAntSortOrder = (sortOrder?: string | null): SortOrder | null => {
  if (sortOrder === 'Asc') return 'ascend';
  if (sortOrder === 'Desc') return 'descend';
  return null;
};

export const fromAntSortOrder = (
  sortOrder?: SortOrder | null,
): ServerSortDefaults['SortOrder'] | undefined => {
  if (sortOrder === 'ascend') return 'Asc';
  if (sortOrder === 'descend') return 'Desc';
  return undefined;
};

export const getColumnSortOrder = <TColumnMap extends Record<string, string>>(
  request: ServerSortRequest,
  columnKey: string,
  columnSelectorMap: TColumnMap,
): SortOrder | null => {
  if (!isColumnSelectorKey(columnSelectorMap, columnKey)) {
    return null;
  }

  if (request.SortKeySelector !== columnSelectorMap[columnKey]) {
    return null;
  }

  return toAntSortOrder(request.SortOrder);
};

export const createServerSortChangeHandler = <
  TRecord,
  TRequest extends ServerSortRequest = ServerSortRequest,
  TColumnMap extends Record<string, string> = Record<string, string>,
>(
  currentRequest: TRequest,
  columnSelectorMap: TColumnMap,
  updateRequest: (nextRequest: TRequest) => void,
  defaultSort: ServerSortDefaults,
): NonNullable<TableProps<TRecord>['onChange']> => {
  return (_pagination, _filters, sorter) => {
    const activeSorter = getActiveSorter(sorter);
    const sorterColumnKey = activeSorter
      ? getSorterColumnKey(activeSorter)
      : undefined;
    const selectedColumnKey =
      sorterColumnKey && isColumnSelectorKey(columnSelectorMap, sorterColumnKey)
        ? sorterColumnKey
        : undefined;
    const nextSortOrder = activeSorter?.order
      ? fromAntSortOrder(activeSorter.order)
      : undefined;
    const nextSortKeySelector =
      selectedColumnKey && nextSortOrder
        ? columnSelectorMap[selectedColumnKey]
        : defaultSort.SortKeySelector;

    updateRequest({
      ...currentRequest,
      PageIndex: 0,
      SortKeySelector: nextSortKeySelector,
      SortOrder: nextSortOrder ?? defaultSort.SortOrder,
    });
  };
};

export const withServerSorter = <TRecord, TColumnKey extends string>(
  columns: ColumnsType<TRecord>,
  request: ServerSortRequest,
  columnSelectorMap: Record<TColumnKey, string>,
): ColumnsType<TRecord> => {
  return columns.map((column) => {
    if ('children' in column && Array.isArray(column.children)) {
      return {
        ...column,
        children: withServerSorter(column.children, request, columnSelectorMap),
      };
    }

    const columnKey = getColumnKey(column);
    const mappedSelector =
      columnKey && isColumnSelectorKey(columnSelectorMap, columnKey)
        ? columnSelectorMap[columnKey]
        : undefined;

    if (!mappedSelector || !columnKey) {
      return column;
    }

    return {
      ...column,
      key: column.key ?? columnKey,
      sorter: true,
      sortOrder: getColumnSortOrder(request, columnKey, columnSelectorMap),
      sortDirections: SORT_DIRECTIONS,
    };
  });
};
